/**
 * Hooks React Query para Voice Commerce M3 — Agent Quick Order.
 *
 * - useProductSearch(query): typeahead con debounce 300ms para buscar productos
 * - useAgentQuickOrder(): mutation que crea order + MP link + WA send
 *
 * Plan: docs/plan/PLAN-VOICE-COMMERCE-M3-AGENT-QUICK-ORDER.md
 */

import { useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@smarty/shared';

// ─── Types ────────────────────────────────────────────────────────────────

export interface QuickProduct {
  id: string;
  name: string;
  price: number;
  category: string;
  image_url: string | null;
  has_variants: boolean;
}

export interface QuickOrderItem {
  product_id?: string;
  product_name: string;
  quantity: number;
  unit_price: number;
}

export interface AgentQuickOrderRequest {
  customer_phone: string;
  customer_name?: string;
  customer_email?: string;
  items: QuickOrderItem[];
  crm_call_id?: string;
}

export interface AgentQuickOrderResponse {
  ok: boolean;
  request_id: string;
  order_id?: string;
  payment_link?: string;
  wa_sent: boolean;
  final_total_usd?: number;
  errors?: string[];
}

// ─── useProductSearch (debounced typeahead) ──────────────────────────────

/**
 * Busca productos del tenant. Usa React Query keepPreviousData implícito
 * (no parpadea entre keystrokes). Debounce hace el caller via useDeferredValue
 * o un wrapper externo (mantenemos el hook puro).
 *
 * Devuelve [] cuando query es vacío o <2 chars (no spam de queries).
 */
export function useProductSearch(query: string, opts: { tenantId?: string | null } = {}) {
  const trimmed = query.trim();
  const enabled = !!opts.tenantId && trimmed.length >= 2;

  return useQuery({
    queryKey: ['pos-quick-order', 'product-search', opts.tenantId, trimmed],
    enabled,
    queryFn: async (): Promise<QuickProduct[]> => {
      // Sanitize ILIKE (escape % _ \)
      const sanitized = trimmed.replace(/[%_\\]/g, (m) => `\\${m}`);
      const { data, error } = await supabase
        .from('products')
        .select('id, name, price, category, image_url, has_variants')
        .eq('tenant_id', opts.tenantId!)
        .eq('is_active', true)
        .or(`name.ilike.%${sanitized}%,category.ilike.%${sanitized}%`)
        .limit(10);
      if (error) throw error;
      return (data ?? []) as QuickProduct[];
    },
    staleTime: 30_000, // cache results 30s para no re-queryear el mismo término
  });
}

// ─── useAgentQuickOrder mutation ─────────────────────────────────────────

export function useAgentQuickOrder() {
  const { tenantId } = useAuth();

  return useMutation({
    mutationFn: async (input: AgentQuickOrderRequest): Promise<AgentQuickOrderResponse> => {
      if (!tenantId) throw new Error('tenant_id required');

      const { data: session } = await supabase.auth.getSession();
      const accessToken = session?.session?.access_token;
      if (!accessToken) throw new Error('No active session');

      const supabaseUrl = (supabase as unknown as { supabaseUrl: string }).supabaseUrl;
      const url = `${supabaseUrl}/functions/v1/pos-quick-order`;

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          tenant_id: tenantId,
          ...input,
        }),
      });

      if (!res.ok) {
        const errBody = await res.text().catch(() => '');
        throw new Error(`pos-quick-order failed: ${res.status} ${errBody.slice(0, 200)}`);
      }

      return await res.json() as AgentQuickOrderResponse;
    },
  });
}

// ─── Helpers para componentes ────────────────────────────────────────────

/**
 * Calcula totals desde una lista de items (subtotal por item, total general).
 * Pure — testeable.
 */
export function calculateQuickOrderTotals(items: QuickOrderItem[]): {
  subtotal: number;
  itemCount: number;
} {
  const subtotal = items.reduce((sum, it) => sum + it.quantity * it.unit_price, 0);
  return { subtotal: Math.round(subtotal * 100) / 100, itemCount: items.length };
}

/**
 * Construye un item nuevo desde un producto seleccionado.
 */
export function buildQuickOrderItemFromProduct(product: QuickProduct, quantity = 1): QuickOrderItem {
  return {
    product_id: product.id,
    product_name: product.name,
    quantity,
    unit_price: product.price,
  };
}

/**
 * Mergea (suma cantidad si existe item con mismo product_id) o append.
 * Pure — testeable.
 */
export function mergeOrAppendItem(items: QuickOrderItem[], newItem: QuickOrderItem): QuickOrderItem[] {
  const existingIdx = items.findIndex((it) => it.product_id && it.product_id === newItem.product_id);
  if (existingIdx >= 0) {
    const copy = [...items];
    copy[existingIdx] = {
      ...copy[existingIdx],
      quantity: copy[existingIdx].quantity + newItem.quantity,
    };
    return copy;
  }
  return [...items, newItem];
}

/** Memoizado helper para components que no usan useMemo en sus parents. */
export function useQuickOrderTotals(items: QuickOrderItem[]) {
  return useMemo(() => calculateQuickOrderTotals(items), [items]);
}
